import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isIdTokenExpired } from '../../session/decode';
import { resolveStorageKeys } from '../constants';
import { AUTH_EVENTS } from '../constants/events';
import type { SessionPhase, SsoConfig, SsoContextValue } from '../types';
import {
  attachDocumentVisibilityListener,
  attachPageActiveListener,
  isDocumentVisible,
  PAGE_SHOW_EVENT,
} from '../utils/browserVisibility';
import { getKeyedTokens } from '../utils/keyedStorage';
import { redirectToSignIn, redirectToSignOut } from '../utils/navigation';
import { refreshSession } from '../utils/refreshSession';
import {
  applyCookieHandoff,
  isAccessTokenLogged,
  isRequestingLocalSignIn,
  localSignIn,
} from '../utils/session';
import {
  getMillisecondsUntilRefresh,
  MAX_TIMEOUT_MS,
} from '../utils/tokenRefreshSchedule';

const isCypressRuntime = () =>
  typeof window !== 'undefined' &&
  Boolean((window as Window & { Cypress?: unknown }).Cypress);

const hasValidAccessTokenForCypress = (
  config: SsoConfig,
  storageKeys: ReturnType<typeof resolveStorageKeys>
) => isCypressRuntime() && isAccessTokenLogged(config, storageKeys);

const notifySessionRecovered = () => {
  window.dispatchEvent(new Event(AUTH_EVENTS.SESSION_RECOVERED));
};

const needsImmediateRefresh = (
  accessToken: string | null,
  idToken: string | null
): boolean => {
  if (!isDocumentVisible()) {
    return false;
  }

  const delayMs = getMillisecondsUntilRefresh(accessToken);

  return delayMs === 0 || isIdTokenExpired(idToken);
};

const hasRefreshToken = (
  storageKeys: ReturnType<typeof resolveStorageKeys>
) => {
  const { refreshToken } = getKeyedTokens(storageKeys);
  return Boolean(refreshToken);
};

export const useSsoSession = (config: SsoConfig): SsoContextValue => {
  const storageKeys = useMemo(() => resolveStorageKeys(config), [config]);
  const configRef = useRef(config);
  configRef.current = config;

  const [logged, setLogged] = useState(() => {
    if (isAccessTokenLogged(config, storageKeys)) {
      return true;
    }

    return hasRefreshToken(storageKeys);
  });
  const [ready, setReady] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionPhase, setSessionPhase] = useState<SessionPhase>('active');
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const sessionPhaseRef = useRef<SessionPhase>(sessionPhase);
  sessionPhaseRef.current = sessionPhase;
  const expiryRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isBootstrappingRef = useRef(false);

  const lockSession = useCallback(() => setSessionPhase('locked'), []);
  const unlockSession = useCallback(() => setSessionPhase('active'), []);

  const clearScheduledExpiryRefresh = useCallback(() => {
    if (expiryRefreshTimeoutRef.current) {
      clearTimeout(expiryRefreshTimeoutRef.current);
      expiryRefreshTimeoutRef.current = null;
    }
  }, []);

  const redirectToSignInAfterSessionLoss = useCallback(() => {
    if (hasValidAccessTokenForCypress(configRef.current, storageKeys)) {
      clearScheduledExpiryRefresh();
      setLogged(true);
      unlockSession();
      scheduleExpiryRefreshRef.current();
      return;
    }

    clearScheduledExpiryRefresh();
    setLogged(false);
    unlockSession();
    redirectToSignIn(configRef.current);
  }, [clearScheduledExpiryRefresh, storageKeys, unlockSession]);

  const handleRefreshTokenFailure = useCallback(() => {
    const { onRefreshTokenError } = configRef.current;

    if (onRefreshTokenError) onRefreshTokenError();

    if (isBootstrappingRef.current) {
      redirectToSignInAfterSessionLoss();
      return;
    }

    lockSession();
  }, [lockSession, redirectToSignInAfterSessionLoss]);

  const handleMissingRefreshToken = useCallback(() => {
    redirectToSignInAfterSessionLoss();
  }, [redirectToSignInAfterSessionLoss]);

  // Refresh token rejected by Cognito (e.g. 400 / revoked / expired).
  const handleInvalidRefreshToken = useCallback(() => {
    const { onRefreshTokenError } = configRef.current;

    if (onRefreshTokenError) onRefreshTokenError();

    clearScheduledExpiryRefresh();

    if (isBootstrappingRef.current) {
      setLogged(false);
      unlockSession();
      redirectToSignOut(configRef.current);
      return;
    }

    lockSession();
  }, [clearScheduledExpiryRefresh, lockSession, unlockSession]);

  const revalidateSessionAfterPageRestore = useCallback(() => {
    if (configRef.current.isExternalPath()) return;

    const accessValid = isAccessTokenLogged(configRef.current, storageKeys);
    const { refreshToken } = getKeyedTokens(storageKeys);

    if (!refreshToken && !accessValid) {
      handleMissingRefreshToken();
      return;
    }

    if (accessValid) {
      unlockSession();
      setLogged(true);
      scheduleExpiryRefreshRef.current();
      return;
    }

    if (refreshToken) {
      void tryRefreshTokenRef.current();
    }
  }, [handleMissingRefreshToken, storageKeys, unlockSession]);

  const scheduleExpiryRefreshRef = useRef<() => void>(() => {});

  const signIn = useCallback(
    (accessToken: string, idToken: string) => {
      localSignIn(
        storageKeys,
        accessToken,
        idToken,
        configRef.current.apiEnv
      );
      unlockSession();
      setLogged(true);
      notifySessionRecovered();
      scheduleExpiryRefreshRef.current();
    },
    [storageKeys, unlockSession]
  );

  const requestSingleSignOn = useCallback(() => {
    redirectToSignIn(configRef.current);
  }, []);

  const requestSingleSignOff = useCallback(() => {
    clearScheduledExpiryRefresh();
    unlockSession();
    setLogged(false);
    redirectToSignOut(configRef.current);
  }, [clearScheduledExpiryRefresh, unlockSession]);

  const tryRefreshTokenRef = useRef<() => Promise<void>>(async () => {});

  const tryRefreshToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    setIsRefreshing(true);

    const refreshPromise = (async () => {
      try {
        const { refreshToken } = getKeyedTokens(storageKeys);
        const result = await refreshSession(
          refreshToken,
          configRef.current.apiEnv
        );

        if (result.ok === false) {
          clearScheduledExpiryRefresh();

          if (result.reason === 'missing') {
            handleMissingRefreshToken();
            return;
          }

          if (result.reason === 'invalid') {
            handleInvalidRefreshToken();
            return;
          }

          handleRefreshTokenFailure();
          return;
        }

        signIn(result.accessToken, result.idToken);
      } finally {
        setIsRefreshing(false);
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;

    return refreshPromise;
  }, [
    clearScheduledExpiryRefresh,
    handleInvalidRefreshToken,
    handleMissingRefreshToken,
    handleRefreshTokenFailure,
    signIn,
    storageKeys,
  ]);

  tryRefreshTokenRef.current = tryRefreshToken;

  const scheduleExpiryRefresh = useCallback(() => {
    clearScheduledExpiryRefresh();

    if (configRef.current.isExternalPath()) return;
    if (sessionPhaseRef.current === 'locked') return;
    if (!isDocumentVisible()) return;

    const { accessToken } = getKeyedTokens(storageKeys);
    const delayMs = getMillisecondsUntilRefresh(accessToken);

    if (delayMs === null) return;

    expiryRefreshTimeoutRef.current = setTimeout(() => {
      expiryRefreshTimeoutRef.current = null;
      tryRefreshTokenRef.current();
    }, Math.min(delayMs, MAX_TIMEOUT_MS));
  }, [clearScheduledExpiryRefresh, storageKeys]);

  scheduleExpiryRefreshRef.current = scheduleExpiryRefresh;

  const revalidateSessionOnActive = useCallback(() => {
    if (configRef.current.isExternalPath()) return;

    const sessionValid = isAccessTokenLogged(configRef.current, storageKeys);

    if (sessionValid) {
      unlockSession();
      setLogged(true);
      scheduleExpiryRefresh();
      return;
    }

    if (sessionPhaseRef.current === 'locked') {
      tryRefreshToken();
      return;
    }

    // Silent refresh: keep UI mounted; do not flip logged to false while Cognito runs.
    tryRefreshToken();
  }, [scheduleExpiryRefresh, storageKeys, tryRefreshToken, unlockSession]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      isBootstrappingRef.current = true;

      try {
        if (configRef.current.isExternalPath()) {
          if (!cancelled) setReady(true);
          return;
        }

        if (isRequestingLocalSignIn(configRef.current.apiEnv)) {
          const handoff = applyCookieHandoff(
            storageKeys,
            configRef.current.apiEnv
          );
          const refreshPresent = hasRefreshToken(storageKeys);

          if (handoff) {
            setLogged(true);
            const { accessToken, idToken } = getKeyedTokens(storageKeys);

            if (refreshPresent && needsImmediateRefresh(accessToken, idToken)) {
              await tryRefreshTokenRef.current();
            } else {
              // Adopting cookie tokens swaps the session, but no refresh runs on this
              // path to announce it, and a same-tab write fires no storage event.
              notifySessionRecovered();
              scheduleExpiryRefresh();
            }
          } else if (refreshPresent) {
            setLogged(true);
            await tryRefreshTokenRef.current();
          } else {
            handleMissingRefreshToken();
          }

          if (!cancelled) setReady(true);
          return;
        }

        const initialLogged = isAccessTokenLogged(
          configRef.current,
          storageKeys
        );
        const refreshPresent = hasRefreshToken(storageKeys);

        if (initialLogged) {
          const { accessToken, idToken } = getKeyedTokens(storageKeys);
          if (
            needsImmediateRefresh(accessToken, idToken) &&
            (refreshPresent || !isCypressRuntime())
          ) {
            await tryRefreshTokenRef.current();
          } else {
            scheduleExpiryRefresh();
          }

          if (!cancelled) setReady(true);
          return;
        }

        if (!initialLogged) {
          if (!refreshPresent) {
            handleMissingRefreshToken();
            if (!cancelled) setReady(true);
            return;
          }

          setLogged(true);
          await tryRefreshTokenRef.current();

          if (!cancelled) setReady(true);
        }
      } finally {
        isBootstrappingRef.current = false;
      }
    };

    bootstrapSession();

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) revalidateSessionAfterPageRestore();
    };

    const detachPageActiveListener = attachPageActiveListener(
      revalidateSessionOnActive
    );

    const detachVisibilityListener = attachDocumentVisibilityListener(
      scheduleExpiryRefresh,
      clearScheduledExpiryRefresh
    );

    const onAuthFailure = () => tryRefreshTokenRef.current();
    const onSessionLock = () => lockSession();

    window.addEventListener(PAGE_SHOW_EVENT, onPageShow);
    window.addEventListener(AUTH_EVENTS.AUTH_FAILURE, onAuthFailure);
    window.addEventListener(AUTH_EVENTS.SESSION_LOCK, onSessionLock);

    return () => {
      cancelled = true;
      detachPageActiveListener();
      detachVisibilityListener();
      clearScheduledExpiryRefresh();

      window.removeEventListener(PAGE_SHOW_EVENT, onPageShow);
      window.removeEventListener(AUTH_EVENTS.AUTH_FAILURE, onAuthFailure);
      window.removeEventListener(AUTH_EVENTS.SESSION_LOCK, onSessionLock);
    };
  }, [
    clearScheduledExpiryRefresh,
    handleMissingRefreshToken,
    lockSession,
    revalidateSessionAfterPageRestore,
    revalidateSessionOnActive,
    scheduleExpiryRefresh,
    storageKeys,
  ]);

  return {
    logged,
    ready,
    isRefreshing,
    sessionPhase,
    lockSession,
    singleSignOff: requestSingleSignOff,
    requestTokenRenew: requestSingleSignOn,
  };
};
